/**
 * Three.js 3D product scene — three 3kg ice-bag GLTF models rotating.
 *
 * Uses GLTFLoader to load /Meshy_AI_3KG_GELO_0411171432_texture.glb (served from public/)
 * Falls back to icosphere placeholders if the model fails to load.
 *
 * Lighting: key + fill + rim + hemi for rich HDR-ish specular highlights.
 * Interaction: pointermove rotates the entire group via parallax lerp.
 */
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  IcosahedronGeometry,
  DirectionalLight,
  AmbientLight,
  Color,
  Group,
  HemisphereLight,
  Object3D,
  ACESFilmicToneMapping,
  SRGBColorSpace,
  Box3,
  Vector3,
} from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { gsap } from 'gsap'

export interface ProductSceneHandle {
  dispose(): void
  getRenderer(): WebGLRenderer
}

const MODEL_URL = '/Meshy_AI_3KG_GELO_0411171432_texture.glb'

export function mountProductScene(canvas: HTMLCanvasElement): ProductSceneHandle {
  // ── Renderer ──
  const renderer = new WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.toneMapping = ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.15
  renderer.outputColorSpace = SRGBColorSpace

  // ── Scene + camera ──
  const scene = new Scene()
  scene.background = null

  const camera = new PerspectiveCamera(35, 16 / 9, 0.1, 100)
  camera.position.set(0, 1.4, 9)
  camera.lookAt(0, 0.2, 0)

  // ── Lights ──
  const key = new DirectionalLight(0xffffff, 3.2)
  key.position.set(5, 8, 6)
  scene.add(key)

  const fill = new DirectionalLight(0x88d0ff, 1.8)
  fill.position.set(-6, 3, 4)
  scene.add(fill)

  const rim = new DirectionalLight(0xffe0b0, 1.4)
  rim.position.set(0, 4, -7)
  scene.add(rim)

  const hemi = new HemisphereLight(0xddeeff, 0xf4fbff, 1.0)
  scene.add(hemi)

  const ambient = new AmbientLight(0xffffff, 0.5)
  scene.add(ambient)

  // ── Product group (3 models side-by-side) ──
  const group = new Group()
  scene.add(group)

  const positions = [
    { x: -3.2, y: 0, z: 0.2, scale: 1.0 },
    { x:  0,   y: 0.1, z: 0.6, scale: 1.25 },
    { x:  3.2, y: 0, z: 0.2, scale: 1.5 },
  ]

  const instances: Object3D[] = []
  const fallbackMeshes: Mesh[] = []

  // Fallback placeholder (used if GLTF fails)
  function buildFallback() {
    positions.forEach((pos) => {
      const geo = new IcosahedronGeometry(0.9 * pos.scale, 1)
      const mat = new MeshPhysicalMaterial({
        color: new Color(0xbff0fb),
        metalness: 0,
        roughness: 0.08,
        transmission: 0.95,
        thickness: 1.4,
        ior: 1.32,
        clearcoat: 1,
        clearcoatRoughness: 0.04,
        transparent: true,
      })
      const mesh = new Mesh(geo, mat)
      mesh.position.set(pos.x, pos.y + 0.4, pos.z)
      group.add(mesh)
      instances.push(mesh)
      fallbackMeshes.push(mesh)
    })
  }

  // ── Load the real GLTF model ──
  const loader = new GLTFLoader()
  loader.load(
    MODEL_URL,
    (gltf) => {
      const template = gltf.scene

      // Normalize scale so the model has ~2 units height regardless of source size
      const box = new Box3().setFromObject(template)
      const size = new Vector3()
      box.getSize(size)
      const maxDim = Math.max(size.x, size.y, size.z) || 1
      const normalizeScale = 2.2 / maxDim

      // Center the model around its own origin
      const center = new Vector3()
      box.getCenter(center)

      // Boost material lighting on existing textured materials
      template.traverse((obj) => {
        const mesh = obj as Mesh
        if (mesh.isMesh) {
          const material = mesh.material as MeshStandardMaterial
          if (material) {
            material.envMapIntensity = 1.4
            // Bump up roughness/metalness tuning for ice feel
            if ('roughness' in material) {
              material.roughness = Math.max(0.15, material.roughness * 0.85)
            }
            material.needsUpdate = true
          }
        }
      })

      // Clone 3 times
      positions.forEach((pos) => {
        const instance = template.clone(true)
        instance.scale.setScalar(normalizeScale * pos.scale)
        instance.position.set(
          pos.x - center.x * normalizeScale * pos.scale,
          pos.y - center.y * normalizeScale * pos.scale + 0.2,
          pos.z - center.z * normalizeScale * pos.scale,
        )
        instance.rotation.y = Math.random() * Math.PI * 2
        group.add(instance)
        instances.push(instance)
      })
    },
    undefined,
    (err) => {
      console.warn('[productScene] GLTF load failed, using fallback:', err)
      buildFallback()
    },
  )

  // ── Drag-to-rotate interaction (Sketchfab-style) ──
  // The group's rotation is integrated from an angular velocity that:
  //   - Receives impulses from the user's drag
  //   - Decays with damping when released (inertia)
  //   - Falls back to a slow ambient Y spin when idle for a moment
  let velX = 0 // angular velocity around X (pitch)
  let velY = 0.004 // angular velocity around Y (yaw) — initial idle drift
  const idleVelY = 0.004 // baseline ambient yaw when nothing is happening
  const damping = 0.93 // applied to velocity each frame after a drag
  const dragSensitivity = 0.0055 // px → radians
  const maxPitch = 0.6 // clamp how far up/down user can pitch (radians)

  let isDragging = false
  let lastX = 0
  let lastY = 0
  let lastInteractionTime = 0
  const idleAfterMs = 1200

  canvas.style.cursor = 'grab'
  canvas.style.touchAction = 'none' // prevents page-scroll hijack on touch

  function onPointerDown(e: PointerEvent) {
    isDragging = true
    lastX = e.clientX
    lastY = e.clientY
    canvas.setPointerCapture(e.pointerId)
    canvas.style.cursor = 'grabbing'
    velX = 0
    velY = 0
    lastInteractionTime = performance.now()
  }

  function onPointerMove(e: PointerEvent) {
    if (!isDragging) return
    const dx = e.clientX - lastX
    const dy = e.clientY - lastY
    lastX = e.clientX
    lastY = e.clientY
    // Direct rotation while dragging — feels 1:1 with cursor
    group.rotation.y += dx * dragSensitivity
    group.rotation.x = Math.max(
      -maxPitch,
      Math.min(maxPitch, group.rotation.x + dy * dragSensitivity),
    )
    // Capture velocity for momentum on release
    velY = dx * dragSensitivity
    velX = dy * dragSensitivity * 0.6
    lastInteractionTime = performance.now()
  }

  function onPointerUp(e: PointerEvent) {
    if (!isDragging) return
    isDragging = false
    try {
      canvas.releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
    canvas.style.cursor = 'grab'
    lastInteractionTime = performance.now()
  }

  canvas.addEventListener('pointerdown', onPointerDown)
  canvas.addEventListener('pointermove', onPointerMove)
  canvas.addEventListener('pointerup', onPointerUp)
  canvas.addEventListener('pointercancel', onPointerUp)

  // ── Resize ──
  function resize() {
    const rect = canvas.getBoundingClientRect()
    const w = rect.width
    const h = rect.height
    if (w === 0 || h === 0) return
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    camera.updateProjectionMatrix()
  }
  const ro = new ResizeObserver(resize)
  ro.observe(canvas)
  resize()

  // ── RAF tick ──
  function tick(_time: number, _dt: number) {
    if (!isDragging) {
      // Decay velocity from any release impulse
      velX *= damping
      velY *= damping

      // After a moment of inactivity, blend the yaw velocity back toward
      // the slow ambient drift so the showcase keeps moving on its own.
      const idleFor = _time - lastInteractionTime
      if (idleFor > idleAfterMs) {
        velY += (idleVelY - velY) * 0.04
        velX += (0 - velX) * 0.04
      }

      // Integrate
      group.rotation.y += velY
      group.rotation.x = Math.max(
        -maxPitch,
        Math.min(maxPitch, group.rotation.x + velX),
      )
    }

    // Gentle vertical bob per-instance — independent of user rotation
    for (let i = 0; i < instances.length; i++) {
      const inst = instances[i]
      if (inst.userData._baseY === undefined) {
        inst.userData._baseY = inst.position.y
      }
      inst.position.y = (inst.userData._baseY as number) + Math.sin(_time * 0.001 + i) * 0.08
    }

    renderer.render(scene, camera)
  }
  gsap.ticker.add(tick)

  return {
    getRenderer: () => renderer,
    dispose() {
      gsap.ticker.remove(tick)
      ro.disconnect()
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('pointerup', onPointerUp)
      canvas.removeEventListener('pointercancel', onPointerUp)
      fallbackMeshes.forEach((m) => {
        m.geometry.dispose()
        ;(m.material as MeshPhysicalMaterial).dispose()
      })
      renderer.dispose()
    },
  }
}
